import { FiLoader, FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAppInfo } from "../helpers/global_service";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Custom styles for ReactQuill read-only display
const quillReadOnlyStyles = `
  .react-quill-readonly .ql-editor {
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    font-family: inherit !important;
    line-height: 1.6 !important;
    color: inherit !important;
    font-size: inherit !important;
  }
  
  .react-quill-readonly .ql-container {
    border: none !important;
    font-size: inherit !important;
  }
  
  .react-quill-readonly .ql-toolbar {
    display: none !important;
  }
  
  .react-quill-readonly .ql-editor.ql-blank::before {
    display: none !important;
  }
`;

interface AppInfoResponse {
  statusCode: number;
  message: string;
  response: string;
}

export default function AboutApp() {
  const { t } = useTranslation();
  const [appInfo, setAppInfo] = useState<AppInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inject ReactQuill read-only styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = quillReadOnlyStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAppInfo({ typeId: 1 });
        setAppInfo(response.data);
      } catch (err) {
        setError(err as string);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRetry = () => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAppInfo({ typeId: 1 });
        setAppInfo(response.data);
      } catch (err) {
        setError(err as string);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full px-4 py-8">
        {/* Content Container */}
        <div className="w-full max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t("pages.aboutApp.title")}
            </h1>
            <div className="w-20 h-1 bg-primaryColor rounded"></div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 md:p-8">
            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <FiLoader className="h-12 w-12 text-primaryColor animate-spin mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("pages.aboutApp.loading")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  {t("pages.aboutApp.loadingDescription")}
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <FiAlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("pages.aboutApp.errorTitle")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                  {error}
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-4 py-2 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 transition-colors duration-200"
                >
                  <FiRefreshCw className="h-4 w-4 mr-2" />
                  {t("pages.aboutApp.retryButton")}
                </button>
              </div>
            )}

            {/* Success State - Display API Content */}
            {appInfo && !loading && !error && (
              <div className="react-quill-readonly">
                <ReactQuill
                  value={appInfo.response}
                  readOnly={true}
                  theme="snow"
                  modules={{
                    toolbar: false,
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                  }}
                />
              </div>
            )}

            {/* Fallback Content - Show if API fails and no retry attempted */}
            {!appInfo && !loading && !error && (
              <div>
                <div className="prose prose-gray dark:prose-invert max-w-none">
                  <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-6">
                    Bossie is a cutting-edge business management platform
                    designed to transform how modern enterprises operate,
                    collaborate, and grow. Our comprehensive suite of tools
                    empowers businesses of all sizes to streamline their
                    operations and achieve unprecedented success.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Our Mission
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    To provide innovative, user-friendly solutions that drive
                    business growth, enhance productivity, and foster
                    collaboration across teams and organizations worldwide.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Key Features
                  </h3>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                    <li>Advanced project management and task tracking</li>
                    <li>Real-time collaboration and communication tools</li>
                    <li>Comprehensive analytics and reporting</li>
                    <li>Secure data management and backup</li>
                    <li>Mobile-first responsive design</li>
                    <li>24/7 customer support</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Why Choose Bossie?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    With years of experience in business technology and a
                    commitment to continuous innovation, Bossie stands as a
                    trusted partner for businesses looking to modernize their
                    operations and achieve sustainable growth in today's
                    competitive landscape.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
